<script lang="ts">
	import { Hr, Input, Label, Spinner } from "flowbite-svelte";
	import { ButtonGroup } from "flowbite-svelte-v1";
	import { createEventDispatcher, getContext, type ComponentEvents } from "svelte";
	import { twMerge } from "tailwind-merge";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import type { Suite } from "#gen/bundles/v1/bundle_pb.js";
	import { type Point } from "#gen/bundles/v1/common_pb.js";
	import { DataType as TokenDataType } from "#gen/common/v1/types_pb.js";
	import type { Workspace } from "#gen/identities/v1/identities_pb.js";
	import type { StaticAssetClient } from "#lib/api/static-assets.js";
	import { dataURIToFile } from "#lib/blob.js";
	import Checkbox from "#lib/components/Checkbox.svelte";
	import Dropzone from "#lib/components/Dropzone.svelte";
	import Icon from "#lib/components/Icon.svelte";
	import Modal from "#lib/components/Modal.svelte";
	import P from "#lib/components/P.svelte";
	import PlaintextEditor from "#lib/components/content-editors/PlaintextEditor.svelte";
	import DropzoneInstructions from "#lib/components/images/DropzoneInstructions.svelte";
	import FocalPointCropper from "#lib/components/images/FocalPointCropper.svelte";
	import ImageCropper from "#lib/components/images/ImageCropper.svelte";
	import { ASPECT_RATIOS, UPLOADABLE_IMAGE_FILE_TYPES } from "#lib/components/images/constants.js";
	import type { ImagePickerChangeEvent } from "#lib/components/images/types.js";
	import { matchAspectRatioPreset, validateUploadFile } from "#lib/components/images/utils.js";
	import { MAX_IMAGE_MEGAPIXELS, bypassCropperForContentType, convertPDFOrEPSImage, getImageDimensions, requireImageConversion } from "#lib/image.js";
	import { Logger } from "#lib/logging.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import Button from "#lib/ui/Button/index.js";
	import type { Writable } from "svelte/store";

	interface $$Events {
		change: CustomEvent<ImagePickerChangeEvent>;
		click: ComponentEvents<Dropzone>["click"];
		replace: CustomEvent;
		close: CustomEvent;
	}

	const dispatch = createEventDispatcher();
	const staticAssetClient: StaticAssetClient = getContext("staticAssetClient");
	const provider: Writable<TokenProvider> = getContext("tokenProvider");
	const workspace: Writable<Workspace> = getContext("workspace");
	const suite: Writable<Suite> = getContext("suite");
	const brandKit: Writable<BrandKit> = getContext("brandKit");

	export let open = false;
	export let value = "";
	export let focalPoint: Point | undefined = undefined;
	export let uploadId = "";
	export let aspectRatio: number | undefined = undefined;
	export let initialAspectRatio: number | undefined = undefined;
	export let caption: string | undefined = undefined;
	export let shape: "circle" | "rectangle" | undefined = undefined;
	export let finalWidth: number | undefined = undefined;
	export let finalHeight: number | undefined = undefined;
	export let contentType: string | null | undefined = undefined;
	export let imageTypeCaption = "image";
	export let fileTypes: string[] = UPLOADABLE_IMAGE_FILE_TYPES;
	export let showFileManagerCheckbox = true;
	export let imageSize = 0;
	export let fileName: string | undefined = undefined;
	export let allowFocalPointSelection = false;
	export let showAspectRatioPicker = false;
	export let preferFreeformAspectRatio = false;

	let changedValue = value;
	let lastError: unknown;
	let imageCropper: ImageCropper;
	let focalCropper: FocalPointCropper;
	let isFetching = false;
	let saveImageChecked = true;
	let editingMode = "Crop image";
	let cropperReady = false;

	$: isFreeform = !ASPECT_RATIOS.find(item => item.ratio !== undefined && item.ratio === matchAspectRatioPreset(aspectRatio))?.name;

	$: changedValue && (lastError = undefined);
	async function onChange(e: ComponentEvents<Dropzone>["change"]) {
		lastError = undefined;
		isFetching = true;
		try {
			await validateUploadFile(e.detail);
			const { url } = await staticAssetClient.upload(e.detail);
			value = url;
			contentType = e.detail.type;
		} catch (e) {
			lastError = e;
		} finally {
			isFetching = false;
		}
	}

	async function onConvert(e: CustomEvent<string>) {
		await handleConvert(e.detail);
	}

	async function onSetFocalPoint(e: CustomEvent<{ data: string; focalX?: number; focalY?: number }>) {
		await handleConvert(e.detail.data);
	}

	async function handleConvert(source: string) {
		lastError = undefined;
		isFetching = true;
		try {
			const dimensions = await getImageDimensions(source);
			if (dimensions.width * dimensions.height > MAX_IMAGE_MEGAPIXELS) {
				throw new Error(`The image exceeds the maximum image area of ${MAX_IMAGE_MEGAPIXELS / Math.pow(10, 6)} megapixels. Use a smaller image and try again.`);
			}
			const file = dataURIToFile(source, "image.png");
			const { url } = await staticAssetClient.upload(file);
			value = url;
			contentType = file.type;
		} catch (e) {
			lastError = e;
		} finally {
			isFetching = false;
		}
	}

	async function getContentType(url: string): Promise<string | null> {
		try {
			const searchParams = new URLSearchParams({ image: url });
			const resp = await fetch(`/api/image-proxy?${searchParams}`, { method: "HEAD" });
			if (!resp.ok) {
				const { message } = await resp.json();
				throw new Error(message);
			}
			return resp.headers.get("Content-Type");
		} catch (err) {
			lastError = err;
		}
		return null;
	}

	async function onContinue() {
		if (!changedValue || $provider?.hasSubstitutionToken(changedValue)) {
			dispatch("change", {
				value: changedValue,
				contentType,
				focalPoint,
			});
			open = false;
			dispatch("close");
			return;
		}
		isFetching = true;
		try {
			contentType = await getContentType(changedValue);
			value = changedValue;
		} catch (e) {
			lastError = e;
		} finally {
			isFetching = false;
		}
	}

	async function onSave() {
		if (editingMode === "Crop image") {
			if (!$provider?.hasSubstitutionToken(value) && !bypassCropperForContentType(contentType)) {
				value = imageCropper.crop();
			}
		}

		if (showFileManagerCheckbox && saveImageChecked) {
			try {
				await staticAssetClient.createStaticAsset({
					name: fileName,
					uploadUrl: value,
					staticAssetId: uploadId,
					mimeType: contentType ?? "",
					orgId: $suite?.orgId ?? $brandKit?.orgId ?? $workspace?.orgId,
					workspaceId: $workspace.id,
					fileSize: imageSize,
				});
			} catch (e) {
				lastError = e;
				Logger.error("Could not save static asset", { err: e });
			}
		}
		dispatch("change", {
			value,
			contentType,
			focalPoint,
		});
		dispatch("close");
		open = false;
	}

	function replace() {
		value = "";
		changedValue = "";
		dispatch("replace");
	}

	function reset() {
		changedValue = "";
		lastError = undefined;
		dispatch("close");
		open = false;
	}

	function handleTokenizedChange(e: CustomEvent<string | undefined>) {
		changedValue = e.detail ?? "";
	}

	function handleChange(e: Event) {
		const target = e.target as HTMLInputElement;
		changedValue = target.value ?? "";
	}
	const modes = ["Crop image", "Set focal point"];

	function setEditingStyle(mode: string) {
		if (editingMode === "Crop image" && cropperReady) {
			value = imageCropper.crop();
		}
		editingMode = mode;
	}

	function resetFocalPoint() {
		focalCropper.reset();
	}
</script>

<Modal size="md" bind:open {lastError} on:close={reset} title={`${value ? "Edit" : "Add"} ${imageTypeCaption}`}>
	<div class="flex aspect-video w-full flex-col gap-4">
		{#if isFetching}
			<div class="flex h-full w-full items-center justify-center bg-gray-50">
				<Spinner />
			</div>
		{:else if value && requireImageConversion(value, contentType)}
			{#await convertPDFOrEPSImage(value, contentType ?? "")}
				<div class="flex h-full w-full items-center justify-center">
					<Spinner size={8} aria-hidden={true} />
				</div>
			{:then svgUrl}
				{#if svgUrl}
					<img src={svgUrl} alt="EPS preview" class="h-full w-full object-contain" />
				{/if}
			{:catch}
				<div class="flex h-full w-full items-center justify-center text-sm text-gray-500">Error loading EPS preview</div>
			{/await}
		{:else if value && !$provider?.hasSubstitutionToken(value)}
			{#if allowFocalPointSelection}
				<div class="mb-2 flex w-full flex-col items-center border-b pb-4">
					<ButtonGroup size="sm" class="shadow-none">
						{#each modes as mode (mode)}
							<Button size="md" color="light" class={twMerge("font-medium", editingMode === mode ? "hover:bg-white" : "bg-gray-100 text-gray-400 hover:text-gray-900")} data-action-name="cropper-editing-filter" data-action-title={mode} on:click={() => setEditingStyle(mode)}>{mode}</Button>
						{/each}
					</ButtonGroup>
				</div>
			{/if}
			{#if editingMode === "Crop image"}
				<div class="flex h-4 w-full flex-row justify-center">
					{#if showAspectRatioPicker && isFreeform}
						<P size="xs">Hold <strong>Shift</strong> + drag to lock the aspect ratio</P>
					{/if}
				</div>
				<ImageCropper bind:this={imageCropper} bind:isReady={cropperReady} {contentType} {value} bind:aspectRatio {initialAspectRatio} {shape} {caption} {finalWidth} {finalHeight} {showAspectRatioPicker} {preferFreeformAspectRatio} on:convert={onConvert} />
			{:else}
				<FocalPointCropper bind:this={focalCropper} {contentType} {value} on:convert={onSetFocalPoint} bind:focalPoint />
			{/if}
		{:else}
			<Dropzone on:change={onChange} bind:lastError on:click fileTypes={fileTypes.join(",")}>
				<div class="flex flex-col items-center justify-center text-gray-500">
					<Icon name="cloud-arrow-up" class="h-10 w-10" />
					<DropzoneInstructions />
				</div>
			</Dropzone>
			<Hr hrClass="my-4 w-full" textSpanClass="text-gray-400">OR</Hr>
			<div class="mb-2 flex flex-col gap-2">
				<Label id="edit-image-url-label" for="edit-image-url" defaultClass="font-bold text-sm">Image URL</Label>
				{#if $provider}
					<PlaintextEditor provider={$provider.withTokensOfType(TokenDataType.STRING, TokenDataType.URL, TokenDataType.IMAGE)} value={changedValue} aria-labelledby="edit-image-url-label" on:change={handleTokenizedChange} />
				{:else}
					<Input type="url" value={changedValue} name="edit-image-url" on:change={handleChange} />
				{/if}
			</div>
		{/if}
		{#if showFileManagerCheckbox}
			<Checkbox bind:checked={saveImageChecked}>Save this image for future usage</Checkbox>
		{/if}
	</div>
	<svelte:fragment slot="footer">
		<div class="flex w-full gap-2" class:justify-between={value && !$provider?.hasSubstitutionToken(value)} class:justify-end={!value || $provider?.hasSubstitutionToken(value)}>
			{#if editingMode === "Set focal point"}
				<Button data-action-name="image-picker-button" data-action-title="Reset focal point" color="alternative" on:click={resetFocalPoint}>Reset focal point</Button>
				<Button data-action-name="image-picker-button" data-action-title="Save" on:click={onSave} disabled={isFetching}>Save</Button>
			{:else if value && !$provider?.hasSubstitutionToken(value)}
				<Button data-action-name="image-picker-button" data-action-title="Replace image" color="alternative" on:click={replace}>Replace {imageTypeCaption}</Button>
				<Button data-action-name="image-picker-button" data-action-title="Save" on:click={onSave} disabled={isFetching || (!requireImageConversion(value, contentType) && !cropperReady)}>Save</Button>
			{:else}
				<Button data-action-name="image-picker-button" data-action-title="Cancel" class="self-end" color="alternative" on:click={() => (open = false)}>Cancel</Button>
				<Button data-action-name="image-picker-button" data-action-title="Continue" color="primary" on:click={onContinue} loading={isFetching}>Continue</Button>
			{/if}
		</div>
	</svelte:fragment>
</Modal>
